import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCanvasMode } from '@/contexts/CanvasModeContext';
import { createClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import PapersCategory from '@/components/canvas/PapersCategory';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCanvasPresence } from '@/hooks/useCanvasPresence';
import { useCanvasItems } from '@/hooks/useCanvasItems';
import { useOfflineCanvasItems } from '@/hooks/useOfflineCanvasItems';
import { CanvasModeDialog } from '@/components/canvas/CanvasModeDialog';
import { CanvasExporter } from '@/components/canvas/CanvasExporter';
import { toast } from 'sonner';
import { 
  Hand, 
  Pen, 
  Type, 
  Sticker, 
  Image, 
  Plus, 
  ZoomIn, 
  ZoomOut, 
  RotateCcw, 
  Trash2, 
  Users, 
  X, 
  FolderOpen, 
  Circle, 
  MousePointer2, 
  Pencil,
  FileText,
  ShoppingBag,
  Package,
  Heart,
  FolderPlus,
  Share2,
  Palette,
  Globe,
  Download
} from 'lucide-react';
import { CanvasImageItem } from '@/components/canvas/CanvasImageItem';
import { CanvasTextItem } from '@/components/canvas/CanvasTextItem';
import { PieMenu, usePieMenu } from '@/components/canvas/PieMenu';
import { useCanvasReactions } from '@/hooks/useCanvasReactions';

interface DrawingStroke {
  id: string;
  points: { x: number; y: number }[];
  color: string;
  width: number;
  tool: 'pen' | 'marker' | 'highlighter';
}

type CanvasItem = {
  id: string;
  position: { x: number; y: number };
  rotation?: number;
  zIndex?: number;
  userId?: string;
} & (
  | { 
    type: 'photo'; 
    data: { 
      url?: string; 
      imageUrl?: string;
      thumbnailUrl?: string;
      fallbackUrl?: string;
      width: number; 
      height: number; 
      ownerId: string; 
      source?: 'upload' | 'papers' | 'stickers'; 
      alt?: string;
    } 
  }
  | { 
    type: 'sticker'; 
    data: { 
      emoji?: string; 
      sticker?: CSSSticker; 
      stickerUrl?: string; 
      width: number; 
      height: number; 
      ownerId: string; 
    } 
  }
  | { 
    type: 'text'; 
    data: { 
      text: string; 
      fontSize: number; 
      fontFamily: string; 
      color: string; 
      width: number; 
      height: number; 
      ownerId: string; 
    } 
  }
);

interface CSSSticker {
  id: string;
  name: string;
  emoji: string;
  backgroundColor: string;
  borderColor?: string;
  textColor?: string;
  animation?: string;
}

interface FomoCanvasProps {
  // Allow passing a user for isolated testing
  testUser?: {
    id: string;
    username: string;
  };
}

const FomoCanvas: React.FC<FomoCanvasProps> = ({ testUser }) => {
  const { user: authUser, profile, refreshSession } = useAuth();
  const user = testUser || authUser; // Prioritize testUser if provided
  const { mode, isComposerMode, isOnlineMode } = useCanvasMode();

  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Mode selection dialog
  const [showModeDialog, setShowModeDialog] = useState(false);

  // Show mode selection dialog on first visit
  useEffect(() => {
    const hasSeenDialog = localStorage.getItem('canvas-mode-dialog-seen');
    if (!hasSeenDialog) {
      setShowModeDialog(true);
      localStorage.setItem('canvas-mode-dialog-seen', 'true');
    }
  }, []);

  // Canvas state
  const [viewportPosition, setViewportPosition] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [selectedTool, setSelectedTool] = useState<'hand' | 'pen' | 'marker' | 'text' | 'shapes' | 'stickers' | 'images'>('hand');
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [draggingItem, setDraggingItem] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Upload state
  const [uploadingImages, setUploadingImages] = useState<Map<string, { progress: number; name: string }>>(new Map());

  // Enhanced Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState<{ x: number; y: number }[]>([]);
  const [strokes, setStrokes] = useState<DrawingStroke[]>([]);
  const [drawingColor, setDrawingColor] = useState('#1a73e8');
  const [brushSize, setBrushSize] = useState(3);
  const [canUndo, setCanUndo] = useState(false);
  const [drawingTool, setDrawingTool] = useState<'pen' | 'marker' | 'highlighter'>('pen');
  const [smoothing, setSmoothing] = useState(true);
  const [pressure, setPressure] = useState(1);

  const isDrawingDisabled = (selectedTool === 'pen' || selectedTool === 'marker') && zoom < 0.2;

  // Use online or offline canvas items based on mode
  const onlineCanvasItems = useCanvasItems('main');
  const offlineCanvasItems = useOfflineCanvasItems();
  const canvasItemsHook = isComposerMode ? offlineCanvasItems : onlineCanvasItems;

  // Canvas items with database persistence (or localStorage in composer mode)
  const {
    items: dbItems,
    addItem: addDbItem,
    updateItem: updateDbItem,
    removeItem: removeDbItem,
    loadViewportSections,
    getDebugSnapshot,
    getDebugMeta
  } = canvasItemsHook;
  
  // Local state for temporary updates during dragging
  const [localItemUpdates, setLocalItemUpdates] = useState<Record<string, Partial<CanvasItem>>>({});
  
  // Convert database items to local canvas item format with local updates applied
  const items: CanvasItem[] = useMemo(() => {
    return dbItems.map(dbItem => {
      const localUpdate = localItemUpdates[dbItem.id];
      return {
        id: dbItem.id,
        type: dbItem.type as any,
        position: localUpdate?.position || dbItem.position,
        rotation: localUpdate?.rotation !== undefined ? localUpdate.rotation : dbItem.rotation,
        zIndex: localUpdate?.zIndex !== undefined ? localUpdate.zIndex : dbItem.zIndex,
        data: localUpdate?.data || dbItem.data,
        userId: dbItem.userId
      };
    });
  }, [dbItems, localItemUpdates]);
  
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  // Viewport-based loading
  const [loadedSections, setLoadedSections] = useState<Set<string>>(new Set());
  const [visibleStrokes, setVisibleStrokes] = useState<DrawingStroke[]>([]);
  const [visibleItems, setVisibleItems] = useState<CanvasItem[]>([]);

  // Image toolkit
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<{ file?: File; url: string; id: string }[]>([]);

  // CSS Stickers
  const [showStickerPanel, setShowStickerPanel] = useState(false);
  // Debug overlay - removed
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
  const [selectedStickerCategory, setSelectedStickerCategory] = useState<'stickers' | 'papers'>('stickers');
  // My Objects panel
  const [showMyObjectsPanel, setShowMyObjectsPanel] = useState(false);
  // Welcome overlay persistent dismissal
  const [welcomeDismissed, setWelcomeDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('canvas-welcome-dismissed') === 'true';
    } catch {
      return false;
    }
  });
  // Pie menu for item interactions
  const pieMenu = usePieMenu();
  const [selectedItemForMenu, setSelectedItemForMenu] = useState<string | null>(null);
  // Sticker drop target tracking
  const [stickerDropTarget, setStickerDropTarget] = useState<string | null>(null);
  const [draggingSticker, setDraggingSticker] = useState<CSSSticker | null>(null);

  // Base sticker library - 10 generic iOS/Android emotes with white sticker outline
  const cssStickers: CSSSticker[] = [
    { id: 'happy', name: 'Happy', emoji: '😊', backgroundColor: '#ffffff', borderColor: '#e0e0e0', textColor: '#333', animation: 'gentle-bounce' },
    { id: 'love', name: 'Love', emoji: '😍', backgroundColor: '#ffffff', borderColor: '#e0e0e0', textColor: '#333', animation: 'heartbeat' },
    { id: 'laugh', name: 'Laugh', emoji: '😂', backgroundColor: '#ffffff', borderColor: '#e0e0e0', textColor: '#333', animation: 'shake' },
    { id: 'cool', name: 'Cool', emoji: '😎', backgroundColor: '#ffffff', borderColor: '#e0e0e0', textColor: '#333', animation: 'slide' },
    { id: 'thinking', name: 'Thinking', emoji: '🤔', backgroundColor: '#ffffff', borderColor: '#e0e0e0', textColor: '#333', animation: 'tilt' },
    { id: 'fire', name: 'Fire', emoji: '🔥', backgroundColor: '#ffffff', borderColor: '#e0e0e0', textColor: '#333', animation: 'flicker' },
    { id: 'star', name: 'Star', emoji: '⭐', backgroundColor: '#ffffff', borderColor: '#e0e0e0', textColor: '#333', animation: 'twinkle' },
    { id: 'thumbs', name: 'Thumbs Up', emoji: '👍', backgroundColor: '#ffffff', borderColor: '#e0e0e0', textColor: '#333', animation: 'pop' },
    { id: 'party', name: 'Party', emoji: '🎉', backgroundColor: '#ffffff', borderColor: '#e0e0e0', textColor: '#333', animation: 'celebrate' },
    { id: 'rocket', name: 'Rocket', emoji: '🚀', backgroundColor: '#ffffff', borderColor: '#e0e0e0', textColor: '#333', animation: 'launch' }
  ];


  // Real-time presence (only in online mode)
  const presenceData = useCanvasPresence(isOnlineMode ? 'main' : 'offline');
  const { activeUsers, updateCursor, updateViewport, userCount, followedUsers, mutualFollowers, goToUser } = presenceData;

  // State for follower panel
  const [showFollowersPanel, setShowFollowersPanel] = useState(false);

  // When switching to Composer Mode, clear upload state (but keep panel open if user wants it)
  useEffect(() => {
    if (isComposerMode) {
      setUploadingImages(new Map());
      // Don't close image panel - users can add images locally in composer mode
    }
  }, [isComposerMode]);

  // Tools configuration
  const tools = useMemo(() => [
    { id: 'hand', icon: Hand, label: 'Navigate' },
    { id: 'pen', icon: Pen, name: 'Draw' },
    { id: 'text', icon: Type, label: 'Text' },
    { id: 'stickers', icon: Sticker, label: 'Stickers' },
    { id: 'images', icon: Image, label: 'Images' },
    { id: 'shop', icon: ShoppingBag, label: 'Shop', disabled: true }
  ], []);

  // Handle tool selection
  const handleToolSelect = useCallback((toolId: string) => {
    setSelectedTool(toolId as any);

    // Special handling for tools with panels
    if (toolId === 'images') {
      // Allow image panel in both modes - just handle storage differently
      try { localStorage.setItem('canvas-welcome-dismissed', 'true'); } catch {}
      setWelcomeDismissed(true);
      setShowImagePanel(true);
      setShowStickerPanel(false);
    } else if (toolId === 'stickers') {
      // Dismiss welcome when user opens stickers
      try { localStorage.setItem('canvas-welcome-dismissed', 'true'); } catch {}
      setWelcomeDismissed(true);
      setShowStickerPanel(true);
      setShowImagePanel(false);
    } else {
      setShowImagePanel(false);
      setShowStickerPanel(false);
    }
  }, [isComposerMode]);


  // Handle zoom
  const handleZoom = useCallback((delta: number) => {
    const newZoom = Math.max(0.1, Math.min(5, zoom + delta));
    setZoom(newZoom);
  }, [zoom]);

  // Handle wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const scrollDelta = e.deltaY > 0 ? 1 / 1.1 : 1.1;
    const newZoom = Math.max(0.1, Math.min(zoom * scrollDelta, 10));

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const newViewportX = viewportPosition.x + mouseX * (1 / zoom - 1 / newZoom);
    const newViewportY = viewportPosition.y + mouseY * (1 / zoom - 1 / newZoom);

    setZoom(newZoom);
    setViewportPosition({ x: newViewportX, y: newViewportY });
  }, [zoom, viewportPosition.x, viewportPosition.y]);

  // Reset view
  const resetView = useCallback(() => {
    setViewportPosition({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  // Hotkey: Shift + D removed

  // Clear canvas
  const clearCanvas = useCallback(() => {
    setStrokes([]);
    // Clear all canvas items from database
    items.forEach(item => removeDbItem(item.id));
    setCurrentStroke([]);
    setSelectedItems([]);
    setVisibleStrokes([]);
    setVisibleItems([]);
    setLoadedSections(new Set());
  }, [items, removeDbItem]);

  // Image optimization function
  const optimizeImage = useCallback(async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        try {
          // Set max dimensions for mobile optimization
          const maxWidth = 800;
          const maxHeight = 600;

          // Calculate new dimensions
          let { width, height } = img;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }

          canvas.width = width;
          canvas.height = height;

          // Draw and compress
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
              if (blob) {
                const optimizedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(optimizedFile);
              } else {
                resolve(file);
              }
            }, 'image/jpeg', 0.8);
          } else {
            resolve(file);
          }
        } catch (error) {
          console.error('Image optimization error:', error);
          resolve(file);
        }
      };

      img.onerror = (error) => {
        console.error('Image load error:', error);
        resolve(file);
      };

      img.src = URL.createObjectURL(file);
    });
  }, []);

  // Handle image file selection
  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('FomoCanvas: Image selection started');
    const files = Array.from(e.target.files || []);
    console.log('FomoCanvas: Selected files:', files.length);

    if (files.length === 0) {
      console.log('FomoCanvas: No files selected');
      return;
    }

    // Filter for image files and check size
    const imageFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isValidSize = file.size <= 25 * 1024 * 1024; // 25MB limit

      console.log(`FomoCanvas: File ${file.name} - isImage: ${isImage}, size: ${Math.round(file.size / 1024 / 1024)}MB`);

      if (!isImage) {
        console.warn(`Skipped non-image file: ${file.name}`);
      }
      if (!isValidSize) {
        console.warn(`Skipped oversized file: ${file.name} (${Math.round(file.size / 1024 / 1024)}MB)`);
      }

      return isImage && isValidSize;
    });

    console.log('FomoCanvas: Valid image files:', imageFiles.length);

    if (imageFiles.length === 0) {
      alert('No valid image files selected. Please select images under 25MB.');
      return;
    }

    // Process images
    const newPreviews: { file?: File; url: string; id: string }[] = [];

    for (const file of imageFiles) {
      try {
        console.log('FomoCanvas: Processing image:', file.name);
        const optimizedFile = await optimizeImage(file);
        const url = URL.createObjectURL(optimizedFile);
        newPreviews.push({ file: optimizedFile, url, id: `temp-${Date.now()}-${Math.random()}` });
        console.log('FomoCanvas: Successfully processed:', file.name);
      } catch (error) {
        console.error('Error processing image:', file.name, error);
      }
    }

    console.log('FomoCanvas: Setting previews:', newPreviews.length);
    // Append new previews to existing ones (don't replace)
    setImagePreviews(prev => [...prev, ...newPreviews]);

    // Clear the input
    e.target.value = '';

    console.log('FomoCanvas: Image import complete. Total previews:', newPreviews.length);
  }, [optimizeImage]);

  // Create plop VFX animation at specific point
  const createPlopVFX = useCallback((x: number, y: number) => {
    const plopElement = document.createElement('div');
    plopElement.className = 'plop-effect';
    plopElement.style.position = 'absolute';
    plopElement.style.left = `${x}px`;
    plopElement.style.top = `${y}px`;
    plopElement.style.pointerEvents = 'none';
    plopElement.style.zIndex = '1000';
    plopElement.style.transform = 'translate(-50%, -50%) scale(0)';
    plopElement.style.width = '120px';
    plopElement.style.height = '120px';
    plopElement.style.borderRadius = '50%';
    plopElement.style.background = 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)';
    plopElement.style.animation = 'plop-animation 0.5s ease-out forwards';

    const container = canvasContainerRef.current;
    if (container) {
      container.appendChild(plopElement);

      // Clean up the element after animation completes
      setTimeout(() => {
        if (plopElement.parentNode) {
          plopElement.parentNode.removeChild(plopElement);
        }
      }, 500);
    }
  }, []);

  // Add image to canvas
  const addImageToCanvas = useCallback(async (file: File, x: number, y: number) => {
    console.log('Adding image to canvas at:', { rawX: x, rawY: y, zoom, viewportPosition, mode: isComposerMode ? 'composer' : 'online' });

    // Generate random tilt between -15 and 15 degrees
    const randomTilt = (Math.random() - 0.5) * 30;
    
    // COMPOSER MODE: Store image as base64 data URL locally
    if (isComposerMode) {
      const itemId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      
      toast.info(`Adding ${file.name} to canvas...`, { id: itemId });
      
      try {
        // Convert image to base64 data URL
        const reader = new FileReader();
        
        reader.onload = async (e) => {
          const dataUrl = e.target?.result as string;
          
          // Load image to get dimensions
          const img = document.createElement('img');
          img.onload = () => {
            const aspectRatio = img.naturalWidth / img.naturalHeight;
            const maxSize = 250;
            
            let width, height;
            if (aspectRatio > 1) {
              width = Math.min(maxSize, img.naturalWidth);
              height = width / aspectRatio;
            } else {
              height = Math.min(maxSize, img.naturalHeight);
              width = height * aspectRatio;
            }
            
            const centeredX = x - width / 2;
            const centeredY = y - height / 2;
            
            const newItem = {
              id: itemId,
              type: 'photo' as const,
              position: { x: centeredX, y: centeredY },
              rotation: randomTilt,
              zIndex: Date.now(),
              data: {
                imageUrl: dataUrl,
                url: dataUrl,
                width,
                height,
                ownerId: user?.id || 'anonymous',
                source: 'local'
              },
              userId: user?.id || 'anonymous',
              createdAt: new Date()
            };
            
            addDbItem(newItem);
            toast.success(`${file.name} added to canvas!`, { id: itemId });
            
            // Show plop VFX
            const rect = canvasContainerRef.current?.getBoundingClientRect();
            if (rect) {
              const screenX = (x - viewportPosition.x) * zoom + rect.left;
              const screenY = (y - viewportPosition.y) * zoom + rect.top;
              createPlopVFX(screenX, screenY);
            }
          };
          
          img.onerror = () => {
            toast.error(`Failed to load ${file.name}`, { id: itemId });
          };
          
          img.src = dataUrl;
        };
        
        reader.onerror = () => {
          toast.error(`Failed to read ${file.name}`, { id: itemId });
        };
        
        reader.readAsDataURL(file);
        return;
      } catch (error) {
        console.error('Error adding image in composer mode:', error);
        toast.error(`Failed to add ${file.name}`, { id: itemId });
        return;
      }
    }
    
    // ONLINE MODE: Upload to Supabase storage
    const uploadId = `upload-${Date.now()}`;
    
    // Add to uploading state
    setUploadingImages(prev => new Map(prev).set(uploadId, { progress: 0, name: file.name }));
    
    // Show upload started toast
    toast.info(`Uploading ${file.name}...`, { id: uploadId });

    try {
      // First, add a temporary placeholder to the canvas with a loading state
      const tempUrl = URL.createObjectURL(file);
      
      // Upload image to Supabase storage
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `canvas-images/${user?.id || 'anonymous'}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      
      console.log('📤 Uploading image to storage:', fileName);
      
      // Update progress to 30%
      setUploadingImages(prev => new Map(prev).set(uploadId, { progress: 30, name: file.name }));
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('canvas-uploads')
        .upload(fileName, file, {
          contentType: file.type,
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Error uploading image:', uploadError);
        
        // Clean up temporary URL
        URL.revokeObjectURL(tempUrl);
        
        // Remove from uploading state
        setUploadingImages(prev => {
          const newMap = new Map(prev);
          newMap.delete(uploadId);
          return newMap;
        });
        
        // Show error with details
        toast.error(`Failed to upload ${file.name}`, {
          id: uploadId,
          description: uploadError.message || 'Check console for details'
        });
        return;
      }
      
      // Update progress to 60%
      setUploadingImages(prev => new Map(prev).set(uploadId, { progress: 60, name: file.name }));

      // Get public URL for the uploaded image
      const { data: { publicUrl } } = supabase.storage
        .from('canvas-uploads')
        .getPublicUrl(fileName);

      console.log('✅ Image uploaded successfully:', publicUrl);
      
      // Update progress to 90%
      setUploadingImages(prev => new Map(prev).set(uploadId, { progress: 90, name: file.name }));

      // Generate a proper UUID for the item
      const itemId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      
      // Add placeholder with estimated dimensions immediately for instant feedback
      const estimatedWidth = 200;
      const estimatedHeight = 200;
      const centeredX = x - estimatedWidth / 2;
      const centeredY = y - estimatedHeight / 2;
      
      const placeholderItem = {
        id: itemId,
        type: 'photo' as const,
        position: { x: centeredX, y: centeredY },
        rotation: randomTilt,
        zIndex: Date.now(),
        data: {
          imageUrl: publicUrl,
          url: publicUrl,
          width: estimatedWidth,
          height: estimatedHeight,
          ownerId: user?.id || 'anonymous',
          source: 'upload',
          isLoading: true // Flag for loading state
        },
        userId: user?.id || 'anonymous',
        createdAt: new Date()
      };
      
      // Add to local state immediately (optimistic update)
      addDbItem(placeholderItem).catch((err) => {
        console.error('❌ Failed to save image item to database:', err);
        toast.error('Failed to save image to canvas');
      });
      
      console.log('📦 Added placeholder image, loading actual dimensions...');

      // Load actual dimensions in background and update
      const img = document.createElement('img');
      img.onload = async () => {
        // Calculate actual aspect ratio and size
        const aspectRatio = img.naturalWidth / img.naturalHeight;
        const maxSize = 250;

        let width, height;
        if (aspectRatio > 1) {
          width = Math.min(maxSize, img.naturalWidth);
          height = width / aspectRatio;
        } else {
          height = Math.min(maxSize, img.naturalHeight);
          width = height * aspectRatio;
        }

        const actualCenteredX = x - width / 2;
        const actualCenteredY = y - height / 2;

        console.log('📐 Updating with actual dimensions:', { width, height });
        
        // Update item with actual dimensions
        updateDbItem(itemId, {
          position: { x: actualCenteredX, y: actualCenteredY },
          data: {
            imageUrl: publicUrl,
            url: publicUrl,
            width,
            height,
            ownerId: user?.id || 'anonymous',
            source: 'upload',
            isLoading: false
          }
        }).catch((err) => {
          console.warn('Failed to update dimensions:', err);
        });
        
        // Clean up temporary URL
        URL.revokeObjectURL(tempUrl);
        
        // Update progress to 100% and remove from uploading state
        setUploadingImages(prev => {
          const newMap = new Map(prev);
          newMap.delete(uploadId);
          return newMap;
        });
        
        // Show success toast
        toast.success(`${file.name} added to canvas!`, { id: uploadId });

        // Show plop VFX animation
        const rect = canvasContainerRef.current?.getBoundingClientRect();
        if (rect) {
          const screenX = (x - viewportPosition.x) * zoom + rect.left;
          const screenY = (y - viewportPosition.y) * zoom + rect.top;
          createPlopVFX(screenX, screenY);
        }
      };

      img.onerror = () => {
        console.error('Failed to load image dimensions, keeping placeholder');
        
        // Clean up
        URL.revokeObjectURL(tempUrl);
        setUploadingImages(prev => {
          const newMap = new Map(prev);
          newMap.delete(uploadId);
          return newMap;
        });
        
        // Keep the item but mark as loaded
        updateDbItem(itemId, {
          data: {
            ...placeholderItem.data,
            isLoading: false
          }
        });
        
        toast.success(`${file.name} added to canvas!`, { id: uploadId });
      };

      // Load image from public URL to get dimensions (non-blocking)
      img.src = publicUrl;

    } catch (error) {
      console.error('Error adding image to canvas:', error);
      
      // Remove from uploading state
      setUploadingImages(prev => {
        const newMap = new Map(prev);
        newMap.delete(uploadId);
        return newMap;
      });
      
      toast.error(`Failed to add ${file.name}`, {
        id: uploadId,
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }, [zoom, viewportPosition, user?.id, createPlopVFX, addDbItem, isComposerMode, updateDbItem]);

// Add CSS sticker to canvas
const addCSSSticker = useCallback((sticker: CSSSticker, x: number, y: number) => {
console.log('Adding CSS sticker to canvas:', { sticker, x, y });
    // Default size for stickers
    const size = 60;
    
    // Generate random tilt between -15 and 15 degrees
    const randomTilt = (Math.random() - 0.5) * 30;
    
    // Center the sticker at the click position
    const centeredX = x - size / 2;
    const centeredY = y - size / 2;
    
    // Create a temporary ID for tracking this upload
    const trackingId = `sticker-${Date.now()}`;
    
    // Show started toast
    toast.info(`Adding sticker...`, { id: trackingId });
    
    try {
      // Generate a proper UUID for the item
      const itemId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      
      const newItem = {
        id: itemId,
        type: 'sticker' as const,
        position: { x: centeredX, y: centeredY },
        rotation: randomTilt,
        zIndex: 1,
        data: {
          emoji: sticker.emoji, // ✅ Add emoji field directly for CanvasImageItem
          sticker: sticker,
          stickerUrl: sticker.emoji,
          width: size,
          height: size,
          ownerId: user?.id || 'anonymous'
        },
        userId: user?.id || 'anonymous',
        createdAt: new Date()
      };
      
      console.log('📦 Adding CSS sticker to database:', newItem);
      
      // Add to database with proper error handling
      addDbItem(newItem).then(() => {
        console.log('✅ Sticker saved to database successfully');
        toast.success(`Sticker added!`, { id: trackingId });
      }).catch(err => {
        console.error('❌ Error saving sticker to database:', err);
        toast.error(`Failed to add sticker`, { id: trackingId });
      });
      
      // Show plop VFX animation at the position
      const rect = canvasContainerRef.current?.getBoundingClientRect();
      if (rect) {
        const screenX = (x - viewportPosition.x) * zoom + rect.left;
        const screenY = (y - viewportPosition.y) * zoom + rect.top;
        createPlopVFX(screenX, screenY);
      }
    } catch (error) {
      console.error('Error adding sticker:', error);
      toast.error(`Failed to add sticker`, { id: trackingId });
    }
  }, [zoom, viewportPosition, user?.id, createPlopVFX, addDbItem]);

  // Add text item to canvas
  const addTextItem = useCallback((x: number, y: number) => {
    console.log('Adding text item to canvas:', { x, y });
    
    // Default text size and properties
    const fontSize = 18;
    const fontFamily = 'Arial, sans-serif';
    const color = '#333333';
    const width = 200;
    const height = 30;
    
    // Center the text at the click position
    const centeredX = x - width / 2;
    const centeredY = y - height / 2;
    
    // Generate a proper UUID for the item
    const itemId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    
    const newItem = {
      id: itemId,
      type: 'text' as const,
      position: { x: centeredX, y: centeredY },
      rotation: 0,
      zIndex: 1,
      data: {
        text: 'Click to edit text',
        content: 'Click to edit text',
        fontSize,
        fontFamily,
        color,
        width,
        height,
        ownerId: user?.id || 'anonymous'
      },
      userId: user?.id || 'anonymous',
      createdAt: new Date()
    };
    
    console.log('📦 Adding text item to database:', newItem);
    addDbItem(newItem);
    createPlopVFX(x, y);
  }, [user?.id, createPlopVFX]);

  // Check if a stroke is in visible area
  const isStrokeVisible = useCallback((stroke: DrawingStroke) => {
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect) return true;

    const buffer = 200; // Small buffer for smooth experience
    const minX = viewportPosition.x - buffer / zoom;
    const maxX = viewportPosition.x + rect.width / zoom + buffer / zoom;
    const minY = viewportPosition.y - buffer / zoom;
    const maxY = viewportPosition.y + rect.height / zoom + buffer / zoom;

    // Check if any point of the stroke is visible
    return stroke.points.some(point =>
      point.x >= minX && point.x <= maxX &&
      point.y >= minY && point.y <= maxY
    );
  }, [viewportPosition.x, viewportPosition.y, zoom]);

  // Debug helper: focus and highlight an item by id
  const focusItemById = useCallback((id: string) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Center the item's center point in the viewport, accounting for zoom
    const itemCenterX = item.position.x + (item as any).data?.width / 2;
    const itemCenterY = item.position.y + (item as any).data?.height / 2;
    const viewportWidthInCanvas = rect.width / zoom;
    const viewportHeightInCanvas = rect.height / zoom;
    const targetViewportX = itemCenterX - viewportWidthInCanvas / 2;
    const targetViewportY = itemCenterY - viewportHeightInCanvas / 2;
    setViewportPosition({ x: targetViewportX, y: targetViewportY });
    setHighlightedItemId(id);
    // Clear highlight after 2 seconds
    window.setTimeout(() => setHighlightedItemId(prev => (prev === id ? null : prev)), 2000);
  }, [items, zoom]);

  // Update visible content when viewport changes
  useEffect(() => {
    console.log('🔄 [useEffect-VisibleContent] Running with:', { viewportX: viewportPosition.x, viewportY: viewportPosition.y, zoom });
    const rect = canvasContainerRef.current?.getBoundingClientRect();

    // Compute visible strokes
    const newVisibleStrokes = strokes.filter(isStrokeVisible);
    setVisibleStrokes(prev => {
      // Only update if changed (shallow compare by length and first/last ids)
      const strokesChanged =
        newVisibleStrokes.length !== prev.length ||
        (newVisibleStrokes[0]?.id !== prev[0]?.id) ||
        (newVisibleStrokes[newVisibleStrokes.length - 1]?.id !== prev[newVisibleStrokes.length - 1]?.id);
      if (strokesChanged) console.log('✏️ [useEffect-VisibleContent] Strokes changed');
      return strokesChanged ? newVisibleStrokes : prev;
    });

    // Compute visible items
    if (rect) {
      const buffer = 200;
      const minX = viewportPosition.x - buffer / zoom;
      const maxX = viewportPosition.x + rect.width / zoom + buffer / zoom;
      const minY = viewportPosition.y - buffer / zoom;
      const maxY = viewportPosition.y + rect.height / zoom + buffer / zoom;

      const newVisibleItems = items.filter(item => {
        const itemRight = item.position.x + item.data.width;
        const itemBottom = item.position.y + item.data.height;
        return itemRight > minX && item.position.x < maxX && itemBottom > minY && item.position.y < maxY;
      });

      setVisibleItems(newVisibleItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, viewportPosition.x, viewportPosition.y, zoom]);

  // Throttle broadcasting viewport to presence to avoid render loops
  const updateViewportRef = useRef(updateViewport);
  useEffect(() => {
    updateViewportRef.current = updateViewport;
  });
  
  useEffect(() => {
    console.log('📡 [useEffect-UpdateViewport] Running with:', { viewportX: viewportPosition.x, viewportY: viewportPosition.y, zoom });
    const t = setTimeout(() => {
      updateViewportRef.current(viewportPosition.x, viewportPosition.y, zoom);
    }, 100);
    return () => clearTimeout(t);
  }, [viewportPosition.x, viewportPosition.y, zoom]);

  // Ensure sections load for current viewport whenever it changes
  const loadSectionsRef = useRef(loadViewportSections);
  useEffect(() => {
    loadSectionsRef.current = loadViewportSections;
  });
  
  useEffect(() => {
    // Skip loading during active interactions to prevent stuttering
    if (isPanning || isDrawing || draggingItem) {
      console.log('📥 [useEffect-LoadSections] Skipped during interaction');
      return;
    }
    console.log('📥 [useEffect-LoadSections] Running with:', { viewportX: viewportPosition.x, viewportY: viewportPosition.y, zoom });
    loadSectionsRef.current(viewportPosition, zoom);
  }, [viewportPosition.x, viewportPosition.y, zoom, isPanning, isDrawing, draggingItem]);

  // On mount: refresh session (if needed) and load initial sections
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshSession();
      } catch (err) {
        console.warn('FomoCanvas: refreshSession failed or not required', err);
      } finally {
        if (!cancelled) {
          // Use current values once on mount
          loadViewportSections({ x: viewportPosition.x, y: viewportPosition.y }, zoom);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mouse event handlers
  // Enhanced drawing with pressure sensitivity and smoothing
  const smoothStroke = useCallback((points: { x: number; y: number }[]) => {
    if (!smoothing || points.length < 3) return points;

    const smoothed = [points[0]];
    for (let i = 1; i < points.length - 1; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const next = points[i + 1];

      // Simple smoothing using quadratic interpolation
      const smoothedX = (prev.x + 2 * curr.x + next.x) / 4;
      const smoothedY = (prev.y + 2 * curr.y + next.y) / 4;

      smoothed.push({ x: smoothedX, y: smoothedY });
    }
    smoothed.push(points[points.length - 1]);
    return smoothed;
  }, [smoothing]);

  const handleMouseDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // Check if the click is on an image sticker - if so, let it handle the event
    const target = e.target as HTMLElement;
    const stickerContainer = target.closest('.sticker-container');
    if (stickerContainer) {
      return; // Let the sticker handle the event
    }

    // Auto-close any open panels when clicking the canvas
    const wasImagePanelOpen = showImagePanel;
    const wasStickerPanelOpen = showStickerPanel;
    setShowImagePanel(false);
    setShowStickerPanel(false);

    // Auto-switch to hand tool when closing panels (unless using drawing tools)
    if ((wasImagePanelOpen || wasStickerPanelOpen) && selectedTool !== 'pen' && selectedTool !== 'marker') {
      setSelectedTool('hand');
    }

    if (isDrawingDisabled) {
      toast.error('Zoom in to draw', { description: 'Drawing is disabled when zoomed out too far to prevent accidental marks.' });
      return;
    }
    const position = getPointerPosition(e);
    if (!position) return;

    const { canvasX, canvasY, clientX, clientY } = position;

    if (selectedTool === 'pen' || selectedTool === 'marker') {
      // Enhanced drawing start with pressure detection
      // Detect pressure from pointer events if available
      const currentPressure = (e as any).pressure || pressure;

      setIsDrawing(true);
      setCurrentStroke([{ x: canvasX, y: canvasY }]);
      setPressure(currentPressure);

      // Prevent default to avoid scrolling on mobile
      e.preventDefault();
    } else if (selectedTool === 'text') {
      addTextItem(canvasX, canvasY);
      setSelectedTool('hand'); // Switch back to hand tool after creating text
    } else if ((selectedTool === 'hand' || wasImagePanelOpen || wasStickerPanelOpen) && !draggingItem) {
      // Start panning only if not dragging an item
      setIsPanning(true);
      setPanStart({ x: clientX, y: clientY });
    }
  }, [selectedTool, zoom, viewportPosition.x, viewportPosition.y, draggingItem, showImagePanel, showStickerPanel, pressure]);

  // Unified pointer event handlers for mouse and touch
  const getPointerPosition = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect) return null;

    let clientX: number, clientY: number;
    if ('touches' in e) {
      // Touch event
      if (e.touches.length === 0) return null;
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      canvasX: (clientX - rect.left) / zoom + viewportPosition.x,
      canvasY: (clientY - rect.top) / zoom + viewportPosition.y,
      clientX,
      clientY
    };
  }, [zoom, viewportPosition.x, viewportPosition.y]);

  const handleMouseMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const position = getPointerPosition(e);
    if (!position) return;

    const { canvasX, canvasY, clientX, clientY } = position;

    // Update cursor position for real-time presence
    updateCursor(canvasX, canvasY);

    if (isPanning) {
      const deltaX = clientX - panStart.x;
      const deltaY = clientY - panStart.y;

      setViewportPosition(prev => ({
        x: prev.x - deltaX / zoom,
        y: prev.y - deltaY / zoom
      }));

      setPanStart({ x: clientX, y: clientY });
    } else if (isDrawing) {
      // Enhanced drawing with distance-based point sampling for smoother strokes
      setCurrentStroke(prev => {
        const lastPoint = prev[prev.length - 1];
        const distance = Math.sqrt(
          Math.pow(canvasX - lastPoint.x, 2) + Math.pow(canvasY - lastPoint.y, 2)
        );

        // Only add point if it's far enough from the last point (reduces jitter)
        if (distance > 2 / zoom) {
          return [...prev, { x: canvasX, y: canvasY }];
        }
        return prev;
      });
    } else if (draggingItem) {
      // Handle item dragging - update local state for smooth dragging
      const newX = canvasX - dragOffset.x;
      const newY = canvasY - dragOffset.y;

      // Update local state for immediate visual feedback (merge with existing updates like zIndex/rotation)
      setLocalItemUpdates(prev => ({
        ...prev,
        [draggingItem]: { 
          ...prev[draggingItem],
          position: { x: newX, y: newY } 
        }
      }));
    }
  }, [isPanning, isDrawing, panStart, zoom, updateCursor, draggingItem, dragOffset, getPointerPosition]);

  const handleMouseUp = useCallback(() => {
    if (isDrawing && currentStroke.length > 1) {
      // Apply smoothing to the completed stroke
      const smoothedPoints = smoothStroke(currentStroke);

      const newStroke: DrawingStroke = {
        id: Date.now().toString(),
        points: smoothedPoints,
        color: drawingColor,
        width: brushSize * pressure, // Apply pressure to stroke width
        tool: drawingTool
      };
      setStrokes(prev => [...prev, newStroke]);
      setCanUndo(true);
      setCurrentStroke([]);

      // Update presence to show drawing completion
      updateCursor(smoothedPoints[smoothedPoints.length - 1].x, smoothedPoints[smoothedPoints.length - 1].y);
    }

    // Handle end of dragging - persist all changes to database
    if (draggingItem && localItemUpdates[draggingItem]) {
      const localUpdate = localItemUpdates[draggingItem];
      // Persist position, zIndex, and rotation together
      updateDbItem(draggingItem, localUpdate);
      console.log('📍 Persisted item changes to database:', { itemId: draggingItem, updates: localUpdate });
      
      // Clear local update
      setLocalItemUpdates(prev => {
        const newUpdates = { ...prev };
        delete newUpdates[draggingItem];
        return newUpdates;
      });
    }

    setIsDrawing(false);
    setIsPanning(false);
    setDraggingItem(null);
  }, [isDrawing, currentStroke, drawingColor, brushSize, drawingTool, pressure, smoothStroke, updateCursor, draggingItem, localItemUpdates, updateDbItem]);

  const handleUndo = useCallback(() => {
    setStrokes(prev => {
      const newStrokes = prev.slice(0, -1);
      setCanUndo(newStrokes.length > 0);
      return newStrokes;
    });
  }, []);

  // Generate section boundaries for visual reference
  const generateSectionBoundaries = useCallback(() => {
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect) return [];

    const boundaries = [];
    const startX = Math.floor(viewportPosition.x / 1000) - 2;
    const endX = Math.floor((viewportPosition.x + rect.width / zoom) / 1000) + 2;
    const startY = Math.floor(viewportPosition.y / 1000) - 2;
    const endY = Math.floor((viewportPosition.y + rect.height / zoom) / 1000) + 2;

    // Vertical lines
    for (let x = startX; x <= endX; x++) {
      const screenX = (x * 1000 - viewportPosition.x) * zoom;
      boundaries.push(
        <line
          key={`v-${x}`}
          x1={screenX}
          y1={0}
          x2={screenX}
          y2={rect.height}
          stroke="#f3f4f6"
          strokeWidth={0.5}
          opacity={0.3}
        />
      );
    }

    // Horizontal lines
    for (let y = startY; y <= endY; y++) {
      const screenY = (y * 1000 - viewportPosition.y) * zoom;
      boundaries.push(
        <line
          key={`h-${y}`}
          x1={0}
          y1={screenY}
          x2={rect.width}
          y2={screenY}
          stroke="#f3f4f6"
          strokeWidth={0.5}
          opacity={0.3}
        />
      );
    }

    return boundaries;
  }, [viewportPosition, zoom]);

  // Handle item mouse down for dragging
  const handleItemMouseDown = useCallback((e: React.MouseEvent | React.TouchEvent, item: CanvasItem) => {
    // Allow dragging if user owns this item (regardless of selected tool for better UX)
    // Check both userId (from DB) and data.ownerId (from new items)
    const isOwner = item.userId === user?.id || item.data?.ownerId === user?.id || item.userId === 'anonymous' || item.data?.ownerId === 'anonymous';
    if (isOwner) {
      e.preventDefault();
      e.stopPropagation();

      // Bring item to front by updating its zIndex in LOCAL state first
      const maxZIndex = dbItems.length ? Math.max(...dbItems.map(i => (i.zIndex ?? 1))) : 1;
      const newZIndex = maxZIndex + 1;
      
      // Update local state immediately for smooth dragging (will persist on mouse up)
      // Preserve existing rotation - don't apply random tilt on pickup
      setLocalItemUpdates(prev => ({
        ...prev,
        [item.id]: { 
          ...prev[item.id], // ✅ Preserve existing updates (position, rotation, etc)
          zIndex: newZIndex
        }
      }));

      // Use getPointerPosition for consistent coordinate handling
      const position = getPointerPosition(e);
      if (position) {
        const { canvasX, canvasY, clientX, clientY } = position;

        // Calculate offset from where the pointer clicked relative to item position
        const offsetX = canvasX - item.position.x;
        const offsetY = canvasY - item.position.y;

        setDraggingItem(item.id);
        setDragOffset({ x: offsetX, y: offsetY });
      }
    }
  }, [dbItems, user?.id, getPointerPosition]);

  // Handle text updates
  const handleUpdateText = useCallback((itemId: string, newText: string) => {
    const item = items.find(i => i.id === itemId);
    if (item && item.type === 'text') {
      updateDbItem(itemId, { 
        data: { ...item.data, text: newText, content: newText } 
      });
      console.log('📝 Updated text in database:', { itemId, newText });
    }
  }, [items, updateDbItem]);

  // Handle item rotation - update local state during rotation, persist on mouse up
  const handleItemRotate = useCallback((itemId: string, rotation: number) => {
    setLocalItemUpdates(prev => ({
      ...prev,
      [itemId]: { 
        ...prev[itemId],
        rotation 
      }
    }));
  }, []);

  // Handle rotation end - persist to database and clear local update
  const handleItemRotateEnd = useCallback((itemId: string) => {
    const localUpdate = localItemUpdates[itemId];
    if (localUpdate && localUpdate.rotation !== undefined) {
      updateDbItem(itemId, { rotation: localUpdate.rotation });
      console.log('🔄 Persisted item rotation to database:', { itemId, rotation: localUpdate.rotation });
      
      // Clear rotation from local updates since it's now persisted to dbItems
      setLocalItemUpdates(prev => {
        const newUpdates = { ...prev };
        if (newUpdates[itemId]) {
          const { rotation, ...rest } = newUpdates[itemId];
          if (Object.keys(rest).length > 0) {
            newUpdates[itemId] = rest;
          } else {
            delete newUpdates[itemId];
          }
        }
        return newUpdates;
      });
    }
  }, [localItemUpdates, updateDbItem]);

  // Handle item deletion
  const handleItemDelete = useCallback((itemId: string) => {
    removeDbItem(itemId);
    console.log('🗑️ Deleted item from database:', itemId);
    // Clear dragging state if the deleted item was being dragged
    if (draggingItem === itemId) {
      setDraggingItem(null);
      setDragOffset({ x: 0, y: 0 });
    }
  }, [draggingItem, removeDbItem]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'z') {
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleUndo]);

  // Add the plop animation style
  useEffect(() => {
    // Create style element for animations if it doesn't exist
    const styleId = 'canvas-animations-style';
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = `
        @keyframes plop-animation {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 0.8;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.6;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.5);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(styleElement);
    }
    
    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-gray-50">
      <div
        ref={canvasContainerRef}
        className="absolute inset-0 bg-white canvas-container"
        style={{ 
          cursor: selectedTool === 'pen' || selectedTool === 'marker' ? 'crosshair' : 
                 isPanning ? 'grabbing' : draggingItem ? 'grabbing' : 'grab',
          backgroundImage: `radial-gradient(circle, #e5e7eb 1px, transparent 1px)`,
          backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
          backgroundPosition: `${-viewportPosition.x * zoom % (20 * zoom)}px ${-viewportPosition.y * zoom % (20 * zoom)}px`
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={(e) => {
          e.preventDefault();
          handleMouseDown(e);
        }}
        onTouchMove={(e) => {
          e.preventDefault();
          handleMouseMove(e);
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          handleMouseUp();
        }}
        onWheel={handleWheel}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDrop={(e) => {
          e.preventDefault();
          const rect = canvasContainerRef.current?.getBoundingClientRect();
          if (!rect) return;
          
          const canvasX = (e.clientX - rect.left) / zoom + viewportPosition.x;
          const canvasY = (e.clientY - rect.top) / zoom + viewportPosition.y;
          
          // Handle image drops
          const imageIndex = e.dataTransfer.getData('image/file');
          if (imageIndex && imagePreviews[parseInt(imageIndex)]?.file) {
            addImageToCanvas(imagePreviews[parseInt(imageIndex)].file!, canvasX, canvasY);
            return;
          }
          
          // Handle CSS sticker drops
          const stickerId = e.dataTransfer.getData('css-sticker');
          if (stickerId) {
            const sticker = cssStickers.find(s => s.id === stickerId);
            if (sticker) {
              addCSSSticker(sticker, canvasX, canvasY);
              return;
            }
          }
          
          // Handle paper drops
          const paperId = e.dataTransfer.getData('paper/id');
          const paperUrl = e.dataTransfer.getData('paper/url');
          if (paperId && paperUrl) {
            console.log('Paper drop detected:', { paperId, paperUrl });
            
            // Create an image to get dimensions
            const img = document.createElement('img');
            img.onload = () => {
              // Generate random tilt between -15 and 15 degrees
              const randomTilt = (Math.random() - 0.5) * 30;
              
              // Calculate dimensions while maintaining aspect ratio
              const aspectRatio = img.naturalWidth / img.naturalHeight;
              const maxSize = 300;
              let width, height;
              
              if (aspectRatio > 1) {
                width = Math.min(maxSize, img.naturalWidth);
                height = width / aspectRatio;
              } else {
                height = Math.min(maxSize, img.naturalHeight);
                width = height * aspectRatio;
              }
              
              // Generate a proper UUID for the item
              const itemId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
              
              // Create a canvas item with proper positioning
              const paperItem = {
                id: itemId,
                position: { x: canvasX, y: canvasY },
                rotation: 0,
                zIndex: Date.now(),
                type: 'photo' as const,
                userId: user?.id || 'anonymous',
                createdAt: new Date(),
                data: {
                  url: paperUrl,
                  width,
                  height,
                  ownerId: user?.id || 'anonymous',
                  source: 'papers'
                }
              };
              
              // Add to canvas
              addDbItem(paperItem);
              createPlopVFX(canvasX, canvasY);
            };
            img.src = paperUrl;
          }
        }}
      >
        {/* Section Boundaries */}
        <svg className="absolute inset-0 pointer-events-none" style={{ width: '100%', height: '100%' }}>
          {generateSectionBoundaries()}
        </svg>
        
        {/* User Cursors - Triangle Style */}
        {activeUsers.map(activeUser => (
          <div
            key={activeUser.userId}
            className="absolute pointer-events-none z-50 transition-all duration-100"
            style={{
              left: (activeUser.cursor.x - viewportPosition.x) * zoom,
              top: (activeUser.cursor.y - viewportPosition.y) * zoom,
              opacity: activeUser.isActive ? 1 : 0.5
            }}
          >
            {/* Triangle Cursor */}
            <div className="relative">
              <svg width="16" height="20" viewBox="0 0 16 20" className="drop-shadow-lg">
                <path
                  d="M0 0 L16 8 L8 12 L4 20 Z"
                  fill={activeUser.color}
                  stroke="white"
                  strokeWidth="1"
                />
              </svg>
              
              {/* Username bubble */}
              <div 
                className="absolute top-5 left-4 bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none"
                style={{ zIndex: 1000 }}
              >
                {activeUser.displayName || activeUser.username}
              </div>
            </div>
          </div>
        ))}
        
        {/* Drawing Mode Indicator - Simple and Clean */}
        {(selectedTool === 'pen' || selectedTool === 'marker') && (
          <div className="absolute top-4 left-4 bg-neutral-900/80 text-white text-sm px-4 py-2 rounded-full font-medium shadow-lg backdrop-blur-sm flex items-center gap-2 z-30">
            <Pencil size={16} />
            <span>Drawing Mode</span>
          </div>
        )}

        {/* Debug panel - Removed */}
        
        {/* Enhanced SVG overlay for drawing strokes with advanced rendering */}
        <svg
          className="absolute inset-0 pointer-events-none"
          style={{
            width: '100%',
            height: '100%',
            transform: `scale(${zoom}) translate(${-viewportPosition.x}px, ${-viewportPosition.y}px)`,
            transformOrigin: 'top left',
          }}
        >
          <defs>
            {/* Enhanced stroke filters for different tools */}
            <filter id="penFilter">
              <feGaussianBlur stdDeviation="0.5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            <filter id="markerFilter">
              <feGaussianBlur stdDeviation="1" result="blur" />
              <feBlend in="SourceGraphic" in2="blur" mode="multiply" />
            </filter>
            <filter id="highlighterFilter">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feBlend in="SourceGraphic" in2="blur" mode="screen" />
            </filter>
          </defs>
          
          {/* Render visible completed strokes with enhanced styling */}
          {visibleStrokes.map((stroke) => {
            const filterId = `${stroke.tool}Filter`;
            const opacity = stroke.tool === 'highlighter' ? 0.4 : stroke.tool === 'marker' ? 0.8 : 1;
            
            return (
              <path
                key={stroke.id}
                d={`M ${stroke.points.map(p => `${p.x} ${p.y}`).join(' L ')}`}
                stroke={stroke.color}
                strokeWidth={stroke.width}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={opacity}
                filter={`url(#${filterId})`}
                style={{
                  mixBlendMode: stroke.tool === 'highlighter' ? 'multiply' : 'normal'
                }}
              />
            );
          })}
          
          {/* Render current stroke being drawn with real-time preview */}
          {isDrawing && currentStroke.length > 1 && (
            <g>
              {/* Main stroke */}
              <path
                d={`M ${currentStroke.map(p => `${p.x} ${p.y}`).join(' L ')}`}
                stroke={drawingColor}
                strokeWidth={brushSize * pressure}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={drawingTool === 'highlighter' ? 0.4 : 0.8}
                filter={`url(#${drawingTool}Filter)`}
                style={{
                  mixBlendMode: drawingTool === 'highlighter' ? 'multiply' : 'normal'
                }}
              />
              {/* Stroke preview shadow for better visibility */}
              <path
                d={`M ${currentStroke.map(p => `${p.x} ${p.y}`).join(' L ')}`}
                stroke="rgba(0,0,0,0.1)"
                strokeWidth={brushSize * pressure + 2}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transform: 'translate(1px, 1px)' }}
              />
            </g>
          )}
        </svg>
        
        {/* Container for all canvas items, applying pan and zoom */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            transform: `scale(${zoom}) translate(${-viewportPosition.x}px, ${-viewportPosition.y}px)`,
            transformOrigin: 'top left',
          }}
        >
          {visibleItems.map(item => (
            <div
              key={item.id}
              className="absolute pointer-events-auto"
              style={{
                transform: `translate(${item.position.x}px, ${item.position.y}px)`,
                zIndex: draggingItem === item.id ? 9999 : (item.zIndex || 1),
                outline: highlightedItemId === item.id ? '2px solid #22d3ee' : 
                         stickerDropTarget === item.id ? '3px dashed #ff69b4' : undefined,
                outlineOffset: highlightedItemId === item.id ? '2px' : 
                              stickerDropTarget === item.id ? '8px' : undefined,
                transition: draggingItem === item.id ? 'none' : 'z-index 0s, outline 0.2s ease',
              }}
              onDragOver={(e) => {
                if (draggingSticker && item.userId !== user?.id && item.data?.ownerId !== user?.id) {
                  e.preventDefault(); // Allow drop
                  setStickerDropTarget(item.id);
                }
              }}
              onDragLeave={() => {
                if (stickerDropTarget === item.id) {
                  setStickerDropTarget(null);
                }
              }}
              onDrop={async (e) => {
                e.preventDefault();
                if (!draggingSticker || !user) return;
                
                // Check if dropping on someone else's item
                if (item.userId === user?.id || item.data?.ownerId === user?.id) {
                  toast.info("Can't add stickers to your own items!");
                  return;
                }
                
                try {
                  // Add sticker as a reaction
                  await supabase
                    .from('canvas_item_reactions')
                    .insert({
                      canvas_item_id: item.id,
                      user_id: user.id,
                      reaction_type: 'sticker',
                      sticker_emoji: draggingSticker.emoji,
                      sticker_data: draggingSticker
                    });
                  
                  // Show pop animation
                  createPlopVFX(item.position.x + (item.data.width || 60) / 2, item.position.y + (item.data.height || 60) / 2);
                  toast.success(`${draggingSticker.emoji} added!`);
                  setStickerDropTarget(null);
                } catch (error) {
                  console.error('Error adding sticker reaction:', error);
                  toast.error('Failed to add sticker');
                }
              }}
            >
              {(item.type === 'photo' || item.type === 'sticker') && (
                <CanvasImageItem
                  item={item}
                  isDragging={draggingItem === item.id}
                  onMouseDown={(e) => handleItemMouseDown(e, item)}
                  onRotate={(rotation) => handleItemRotate(item.id, rotation)}
                  onRotateEnd={() => handleItemRotateEnd(item.id)}
                  onDelete={handleItemDelete}
                  onLongPressStart={(e) => {
                    // Only enable pie menu for photos, not stickers
                    if (item.type === 'photo') {
                      pieMenu.handleLongPressStart(e);
                      setSelectedItemForMenu(item.id);
                    }
                  }}
                  onLongPressMove={(e) => {
                    // Track movement to cancel long press if dragging
                    if (item.type === 'photo') {
                      pieMenu.handleLongPressMove(e);
                    }
                  }}
                  onLongPressEnd={pieMenu.handleLongPressEnd}
                />
              )}
              {item.type === 'text' && (
                <CanvasTextItem
                  item={item}
                  isDragging={draggingItem === item.id}
                  onMouseDown={(e) => handleItemMouseDown(e, item)}
                  onClick={() => {}}
                  onUpdateText={handleUpdateText}
                />
              )}
            </div>
          ))}
        </div>

        {/* Debug toggle button - Removed */}

        {/* Welcome message - Mobile Optimized (only show if not dismissed) */}
        {strokes.length === 0 && items.length === 0 && !welcomeDismissed && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center pointer-events-auto px-4 sm:px-6">
            <div className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg border border-white/50 backdrop-blur-sm relative max-w-xs sm:max-w-md md:max-w-lg w-full mx-auto">
              {/* Close button */}
              <button
                onClick={() => {
                  // Persist dismissal
                  try { localStorage.setItem('canvas-welcome-dismissed', 'true'); } catch {}
                  setWelcomeDismissed(true);
                }}
                className="absolute top-2 right-2 sm:top-3 sm:right-3 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors text-base sm:text-lg"
              >
                ×
              </button>
              
              <div className="text-2xl sm:text-3xl md:text-4xl mb-2 sm:mb-3 md:mb-4">🌟</div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-1 sm:mb-2">Welcome to Explore</h1>
              <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3 md:mb-4">Your infinite online canvas</p>
              <div className="text-xs sm:text-sm text-gray-500 space-y-0.5 sm:space-y-1 mb-2 sm:mb-3 md:mb-4">
                <p>• Explore other users posts</p>
                <p>• Post your own content</p>
                <p>• React with stickers</p>
                <p>• Draw and create your own little part of FOMO</p>
              </div>
              <div className="text-xs text-gray-400 italic border-t border-gray-200/50 pt-2 sm:pt-3">
                ⚠️ This feature is very early in development
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Floating Toolbar - Mobile Optimized */}
      <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 z-50 pb-safe">
        <div className="bg-white/95 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-2xl border border-gray-200/50 p-1 sm:p-1.5 md:p-2 flex items-center gap-0.5 sm:gap-1 md:gap-2 max-w-[90vw] overflow-x-auto scrollbar-hide">
          {tools.map((tool) => (
            <Button
              key={tool.id}
              variant="ghost"
              size="lg"
              disabled={tool.disabled || (isDrawingDisabled && (tool.id === 'pen' || tool.id === 'marker'))}
              className={`relative w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg sm:rounded-xl md:rounded-2xl transition-all duration-300 transform active:scale-95 sm:hover:scale-110 flex-shrink-0 ${ 
                (tool.disabled || (isDrawingDisabled && (tool.id === 'pen' || tool.id === 'marker'))) ? 'opacity-50 cursor-not-allowed' : '' } ${
                selectedTool === tool.id 
                  ? 'bg-gradient-to-br from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-xl scale-105 sm:scale-110 ring-2 ring-indigo-300/50' 
                  : 'hover:bg-gray-100/80 text-gray-700 hover:text-gray-900 shadow-lg hover:shadow-xl'
              }`}
              onClick={(e) => {
                e.stopPropagation(); // Prevent canvas click handler from closing panels
                if (tool.disabled) {
                  toast.error(`${tool.label || tool.name} is coming soon`);
                  return;
                }
                if ((tool.id === 'pen' || tool.id === 'marker') && isDrawingDisabled) {
                  toast.error('Zoom in to draw');
                  return;
                }
                handleToolSelect(tool.id);
              }}
            >
              <tool.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" />
              {selectedTool === tool.id && (
                <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-2 border-white" />
              )}
            </Button>
          ))}
          
          {/* Drawing Tool Variants for Pen/Marker - Hidden on small screens */}
          {(selectedTool === 'pen' || selectedTool === 'marker') && (
            <>
              <div className="hidden sm:block w-px h-6 sm:h-8 bg-gray-300/60 mx-0.5 sm:mx-1" />
              <div className="hidden sm:flex items-center gap-0.5 sm:gap-1">
                {[{id: 'highlighter', icon: Pen, name: 'Highlighter'}].map((variant) => (
                  <Button
                    key={variant.id}
                    variant="ghost"
                    size="sm"
                    className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-md sm:rounded-lg transition-all duration-200 flex-shrink-0 ${
                      drawingTool === variant.id
                        ? 'bg-indigo-100 text-indigo-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setDrawingTool(variant.id as 'pen' | 'marker' | 'highlighter')}
                    title={variant.name}
                  >
                    <variant.icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
                  </Button>
                ))}
              </div>
              
              {/* Brush Size Controls - Simplified on mobile */}
              <div className="hidden sm:block w-px h-6 sm:h-8 bg-gray-300/60 mx-0.5 sm:mx-1" />
              <div className="flex items-center gap-0.5 sm:gap-1">
                {[2, 4, 8, 16].slice(0, 3).map((size) => (
                  <Button
                    key={size}
                    variant="ghost"
                    size="sm"
                    className={`p-1 sm:p-1.5 md:p-2 rounded-md sm:rounded-lg transition-colors flex-shrink-0 ${brushSize === size ? 'bg-indigo-200' : ''} ${isDrawingDisabled ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                    onClick={() => setBrushSize(size)}
                    title={`Brush size: ${size}px`}
                  >
                    <div 
                      className="rounded-full bg-current"
                      style={{ 
                        width: Math.min(size / 3 + 2, 8), 
                        height: Math.min(size / 3 + 2, 8) 
                      }}
                    />
                  </Button>
                ))}
              </div>
            </>
          )}

        </div>
      </div>

      {/* Bottom Right Controls (Zoom, Undo, Mode) - Compact */}
      <div className="absolute bottom-4 sm:bottom-6 right-2 sm:right-4 md:right-6 z-50 flex flex-col gap-1 sm:gap-2 pb-safe">
        <div className="bg-white/95 backdrop-blur-sm rounded-full shadow-lg border border-gray-300 p-0.5 sm:p-1 flex items-center gap-0.5 sm:gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleZoom(-0.2)}
            className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full p-0 hover:bg-gray-100 active:scale-95"
          >
            <ZoomOut className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-gray-700" />
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleZoom(0.2)}
            className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full p-0 hover:bg-gray-100 active:scale-95"
          >
            <ZoomIn className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-gray-700" />
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleUndo}
            disabled={!canUndo}
            className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full p-0 hover:bg-gray-100 disabled:opacity-50 active:scale-95"
            title="Undo last stroke (Ctrl+Z)"
          >
            <RotateCcw className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-gray-700" />
          </Button>

          <div className="w-px h-4 sm:h-6 bg-gray-300 mx-0.5 sm:mx-1" />

          {/* Compact Mode Toggle attached to Zoom UI */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowModeDialog(true)}
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full p-0 bg-white/90"
            title={isComposerMode ? 'Switch mode (Composer)' : 'Switch mode (Online)'}
          >
            {isComposerMode ? (
              <Palette className="w-3.5 h-3.5 text-gray-800" />
            ) : (
              <Globe className="w-3.5 h-3.5 text-gray-800" />
            )}
          </Button>

        </div>
      </div>

      {/* Mode Dialog */}
      <CanvasModeDialog 
        open={showModeDialog} 
        onClose={() => setShowModeDialog(false)} 
      />

      {/* Removed separate top-right and mobile mode toggles to save space */}

      {/* User Presence - Mobile Optimized (Online Mode Only) */}
      {isOnlineMode && (
        <div className="absolute top-16 sm:top-20 right-2 sm:right-4 z-40 pt-safe flex flex-col gap-2">
          <div className="bg-white/95 backdrop-blur-sm rounded-full shadow-lg border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2">
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
              G
            </div>
          <Badge 
            variant="secondary" 
            className="bg-gray-200 text-gray-800 border-0 font-medium cursor-pointer hover:bg-gray-300 transition-colors text-xs sm:text-sm"
            onClick={() => setShowFollowersPanel(prev => !prev)}
          >
            <Users className="w-3 h-3 mr-0.5 sm:mr-1 text-gray-700" />
            <span className="hidden xs:inline">{userCount} online</span>
            <span className="xs:hidden">{userCount}</span>
            {followedUsers.length > 0 && (
              <span className="ml-1 bg-indigo-500 text-white rounded-full text-xs px-1.5 inline-flex items-center justify-center">
                {followedUsers.length}
              </span>
            )}
          </Badge>
        </div>
        
        {/* My Objects Button */}
        <div 
          className="bg-white/95 backdrop-blur-sm rounded-full shadow-lg border border-gray-300 px-2 sm:px-3 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setShowMyObjectsPanel(prev => !prev)}
        >
          <Package className="w-4 h-4 text-gray-700" />
          <span className="text-xs sm:text-sm font-medium text-gray-800">
            {items.filter(item => item.userId === user?.id || item.data?.ownerId === user?.id).length} Objects
          </span>
        </div>
        </div>
      )}
      
      {/* My Objects Panel */}
      {showMyObjectsPanel && (() => {
        const myObjects = items.filter(item => item.userId === user?.id || item.data?.ownerId === user?.id);
        return (
          <div className="absolute top-48 right-4 z-40 w-80 max-h-96 bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b border-gray-200">
              <h3 className="font-medium">My Objects</h3>
              <button 
                onClick={() => setShowMyObjectsPanel(false)}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto max-h-80">
              {myObjects.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No objects placed yet
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {myObjects.map(item => (
                    <div 
                      key={item.id}
                      className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => {
                        // Pan to the object
                        setViewportPosition({
                          x: item.position.x - window.innerWidth / (2 * zoom),
                          y: item.position.y - window.innerHeight / (2 * zoom)
                        });
                        setHighlightedItemId(item.id);
                        setTimeout(() => setHighlightedItemId(null), 2000);
                      }}
                    >
                      <div className="flex items-start gap-3">
                        {/* Preview */}
                        <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center text-2xl flex-shrink-0 border border-gray-200">
                          {item.type === 'sticker' && (item.data?.emoji || item.data?.sticker?.emoji) ? (
                            <span>{item.data?.emoji || item.data?.sticker?.emoji}</span>
                          ) : item.type === 'photo' ? (
                            <img 
                              src={item.data?.imageUrl || item.data?.url || item.data?.thumbnailUrl} 
                              alt="Item preview"
                              className="w-full h-full object-cover rounded"
                            />
                          ) : (
                            <FileText className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 capitalize">
                            {item.type}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Position: ({Math.round(item.position.x)}, {Math.round(item.position.y)})
                          </div>
                          {item.rotation && (
                            <div className="text-xs text-gray-500">
                              Rotation: {Math.round(item.rotation)}°
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}
      
      {/* Followers Panel */}
      {showFollowersPanel && (
        <div className="absolute top-32 right-4 z-40 w-64 bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between p-3 border-b border-gray-200">
            <h3 className="font-medium">People You Follow</h3>
            <button 
              onClick={() => setShowFollowersPanel(false)}
              className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
            >
              <X size={14} />
            </button>
          </div>
          
          <div className="max-h-80 overflow-y-auto">
            {followedUsers.length > 0 ? (
              <div className="p-2 space-y-1">
                {followedUsers.map(user => (
                  <div 
                    key={user.userId}
                    className={`flex items-center p-2 rounded-lg ${user.isMutual ? 'bg-indigo-50 hover:bg-indigo-100' : 'hover:bg-gray-50'} transition-colors`}
                  >
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center mr-2" 
                      style={{ backgroundColor: user.color }}
                    >
                      <span className="text-white text-xs font-medium">
                        {user.displayName?.[0] || user.username[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.displayName || user.username}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {user.isFollowing ? 'Follows you' : ''}
                      </p>
                    </div>
                    {user.isMutual && (
                      <button 
                        onClick={() => {
                          // Use the goToUser function - trigger the message to follow user
                          goToUser(user.userId);
                          
                          // Animate to the user's viewport position directly from their presence data
                          if (user.viewport) {
                            setViewportPosition({ 
                              x: user.viewport.x, 
                              y: user.viewport.y 
                            });
                            setZoom(user.viewport.zoom || 1);
                            toast.info(`Following ${user.displayName || user.username}`);
                          }
                        }}
                        className="text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 p-1 rounded transition-colors"
                        title="Go to this user"
                      >
                        <MousePointer2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                <p>No followed users online</p>
              </div>
            )}
          </div>
          
          {mutualFollowers.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500 mb-1">Mutual followers can collaborate with you</p>
              <div className="flex flex-wrap gap-1">
                {mutualFollowers.map(user => (
                  <div 
                    key={user.userId}
                    className="w-7 h-7 rounded-full overflow-hidden border border-white flex items-center justify-center"
                    style={{ backgroundColor: user.color }}
                    title={`${user.displayName || user.username} (mutual follower)`}
                  >
                    <span className="text-white text-xs">
                      {user.displayName?.[0] || user.username[0]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Upload Progress Indicators (Online Mode Only) */}
      {isOnlineMode && uploadingImages.size > 0 && (
        <div className="absolute top-32 right-4 z-40 space-y-2">
          {Array.from(uploadingImages.entries()).map(([id, { progress, name }]) => (
            <div key={id} className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 p-3 min-w-[200px]">
              <div className="text-xs text-gray-600 mb-1 truncate">{name}</div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 transition-all"
                  style={{ width: `${Math.round(progress)}%` }}
                />
              </div>
              <div className="text-right text-[10px] text-gray-500 mt-1">{Math.round(progress)}%</div>
            </div>
          ))}
        </div>
      )}

      {/* Image Panel */}
      {showImagePanel && (
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-50 pointer-events-auto">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200/50 p-4 max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Image Toolkit</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowImagePanel(false)}
                className="w-8 h-8 p-0 rounded-full hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {/* File Input */}
            <div className="mb-4">
              <input
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                onChange={handleImageSelect}
                className="hidden"
                id="image-upload"
                {...({ webkitdirectory: "" } as any)}
              />
              <label
                htmlFor="image-upload"
                className="flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-indigo-400 hover:bg-indigo-50/50 cursor-pointer transition-colors"
              >
                <div className="text-center">
                  <FolderOpen className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Select images from folder</p>
                  <p className="text-xs text-gray-400">Drag images to canvas</p>
                </div>
              </label>
            </div>
            
            {/* Alternative: Individual Files */}
            <div className="mb-4">
              <input
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                onChange={handleImageSelect}
                className="hidden"
                id="image-files"
              />
              <label
                htmlFor="image-files"
                className="flex items-center justify-center w-full p-3 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 cursor-pointer transition-colors"
              >
                <div className="text-center">
                  <Image className="w-6 h-6 text-indigo-600 mx-auto mb-1" />
                  <p className="text-sm text-indigo-700">Or select individual images</p>
                </div>
              </label>
            </div>

            {/* Debug Info */}
            <div className="mb-2 text-xs text-gray-500">
              Loaded images: {imagePreviews.length}
            </div>
            
            {/* Image Thumbnails */}
            {imagePreviews.length > 0 ? (
              <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {imagePreviews.map((preview, index) => (
                  <div
                    key={index}
                    className="relative group cursor-pointer select-none"
                    style={{ touchAction: 'manipulation' }}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('image/file', index.toString());
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Mobile fallback - add image to center of viewport
                      if (preview.file) {
                        const centerX = viewportPosition.x + (window.innerWidth / 2) / zoom;
                        const centerY = viewportPosition.y + (window.innerHeight / 2) / zoom;
                        addImageToCanvas(preview.file, centerX, centerY);
                        setShowImagePanel(false);
                      }
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      // Trigger image addition for mobile
                      if (preview.file) {
                        const centerX = viewportPosition.x + (window.innerWidth / 2) / zoom;
                        const centerY = viewportPosition.y + (window.innerHeight / 2) / zoom;
                        addImageToCanvas(preview.file, centerX, centerY);
                        setShowImagePanel(false);
                      }
                    }}
                  >
                    <img
                      src={preview.url}
                      alt={`Preview ${index}`}
                      className="w-full h-16 object-cover rounded-lg border border-gray-200 group-hover:border-indigo-400 transition-colors"
                      onError={(e) => console.error('Image load error:', preview.url)}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg transition-colors flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 text-white text-xs bg-black/50 px-2 py-1 rounded">
                        Drag to canvas
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 text-center py-4">
                No images loaded yet. Select images above.
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSS Sticker Panel */}
      {showStickerPanel && (
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200/50 p-4 max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-purple-600 via-pink-500 to-indigo-600 bg-clip-text text-transparent">Stickers</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowStickerPanel(false)}
                className="w-8 h-8 p-0 rounded-full hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Category Tabs */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={selectedStickerCategory === 'stickers' ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedStickerCategory('stickers')}
                className={`flex-1 rounded-xl shadow-sm ${selectedStickerCategory === 'stickers' ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700' : 'hover:bg-purple-50 hover:text-purple-700'} transition-all duration-300 text-xs font-medium py-2`}
              >
                <Sticker className="w-4 h-4 mr-1" />
                Stickers
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled
                className="flex-1 rounded-xl shadow-sm opacity-50 cursor-not-allowed text-xs font-medium py-2"
              >
                <FileText className="w-4 h-4 mr-1" />
                Papers
              </Button>
            </div>
            
            {selectedStickerCategory === 'papers' ? (
              <PapersCategory 
                onPaperSelect={(paper) => {
                  // Here we can handle paper selection
                  const centerX = viewportPosition.x + (window.innerWidth / 2) / zoom;
                  const centerY = viewportPosition.y + (window.innerHeight / 2) / zoom;
                  
                  // Add paper to canvas
                  const img = document.createElement('img');
                  img.src = paper.url;
                  img.onload = () => {
                    // Generate random tilt
                    const randomTilt = (Math.random() - 0.5) * 30;
                    
                    // Calculate dimensions while maintaining aspect ratio
                    const aspectRatio = img.naturalWidth / img.naturalHeight;
                    const maxSize = 300;
                    let width, height;
                    
                    if (aspectRatio > 1) {
                      width = Math.min(maxSize, img.naturalWidth);
                      height = width / aspectRatio;
                    } else {
                      height = Math.min(maxSize, img.naturalHeight);
                      width = height * aspectRatio;
                    }
                    
                    // Create a canvas item with proper positioning
                    const paperItem = {
                      id: `image-${Date.now()}`,
                      position: { x: centerX, y: centerY },
                      rotation: randomTilt,
                      zIndex: Date.now(),
                      type: 'photo' as const,
                      userId: user?.id || 'anonymous',
                      createdAt: new Date(),
                      data: {
                        url: paper.url,
                        width,
                        height,
                        ownerId: user?.id || 'anonymous',
                        source: 'papers'
                      }
                    };
                    
                    // Add to canvas
                    addDbItem(paperItem);
                    console.log('📦 Adding sticker paper to database:', paperItem);
                    createPlopVFX(centerX, centerY);
                    setShowStickerPanel(false);
                  };
                }}
                onClose={() => setShowStickerPanel(false)}
              />
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">Drag animated stickers to your canvas</p>

                {/* CSS Sticker Grid */}
                <div className="grid grid-cols-5 gap-3 max-h-64 overflow-y-auto">
                  {cssStickers.map((sticker) => (
                <div
                  key={sticker.id}
                  className={`sticker-preview css-sticker-${sticker.animation} relative group cursor-grab active:cursor-grabbing flex items-center justify-center text-3xl hover:scale-110 transition-all duration-200 w-14 h-14 select-none`}
                  style={{
                    background: 'transparent',
                    color: '#333',
                    touchAction: 'manipulation'
                  }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('css-sticker', sticker.id);
                    e.currentTarget.style.transform = 'scale(0.9) rotate(5deg)';
                    e.currentTarget.style.opacity = '0.7';
                    setDraggingSticker(sticker); // Track which sticker is being dragged
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                    e.currentTarget.style.opacity = '1';
                    setDraggingSticker(null); // Clear dragging state
                    setStickerDropTarget(null); // Clear drop target
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Mobile fallback - add sticker to center of viewport
                    const centerX = viewportPosition.x + (window.innerWidth / 2) / zoom;
                    const centerY = viewportPosition.y + (window.innerHeight / 2) / zoom;
                    addCSSSticker(sticker, centerX, centerY);
                    setShowStickerPanel(false);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.transform = 'scale(0.95) rotate(2deg)';
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                    // Trigger click for mobile
                    const centerX = viewportPosition.x + (window.innerWidth / 2) / zoom;
                    const centerY = viewportPosition.y + (window.innerHeight / 2) / zoom;
                    addCSSSticker(sticker, centerX, centerY);
                    setShowStickerPanel(false);
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'scale(0.95) rotate(2deg)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                  }}
                  title={sticker.name}
                >
                  {sticker.emoji}
                  <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 text-xs text-gray-600 bg-white px-2 py-1 rounded shadow-lg whitespace-nowrap transition-opacity z-10">
                    {sticker.name}
                  </div>
                </div>
              ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* SVG Filters for Advanced Sticker Effects */}
      <svg height="0" width="0" style={{ position: 'absolute' }}>
        <defs>
          <filter id="pointLight">
            <feGaussianBlur stdDeviation="1" result="blur" />
            <feSpecularLighting result="spec" in="blur" specularExponent="100" specularConstant="0.1" lightingColor="white">
              <fePointLight x="100" y="100" z="300" />
            </feSpecularLighting>
            <feBlend in="spec" in2="SourceGraphic" mode="screen" result="lit" />
            <feComposite in="lit" in2="SourceAlpha" operator="in" />
          </filter>

          <filter id="pointLightFlipped">
            <feGaussianBlur stdDeviation="10" result="blur" />
            <feSpecularLighting result="spec" in="blur" specularExponent="100" specularConstant="0.7" lightingColor="white">
              <fePointLight x="100" y="100" z="300" />
            </feSpecularLighting>
            <feBlend in="spec" in2="SourceGraphic" mode="screen" result="lit" />
            <feComposite in="lit" in2="SourceAlpha" operator="in" />
          </filter>

          <filter id="stickerDropShadow">
            <feDropShadow dx="2" dy="4" stdDeviation="3" floodColor="black" floodOpacity="0.6" />
          </filter>

          <filter id="expandAndFill">
            <feOffset dx="0" dy="0" in="SourceAlpha" result="shape" />
            <feFlood floodColor="rgb(179, 179, 179)" result="flood" />
            <feComposite operator="in" in="flood" in2="shape" />
          </filter>

          <filter id="outerStroke">
            <feMorphology operator="dilate" radius="3" in="SourceAlpha" result="expanded" />
            <feColorMatrix in="expanded" values="0 0 0 0 1  0 0 0 0 1  0 0 0 1 0" result="whiteExpanded" />
            <feComposite operator="xor" in="whiteExpanded" in2="SourceAlpha" result="outerStroke" />
            <feComposite operator="over" in="SourceGraphic" in2="outerStroke" />
          </filter>
        </defs>
      </svg>
      
      {/* CSS Animations for Stickers */}
      <style>{`
        /* Subtle sticker animations */
        @keyframes gentle-bounce {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-3px); }
        }
        
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0px); }
          25% { transform: translateX(-2px); }
          75% { transform: translateX(2px); }
        }
        
        @keyframes slide {
          0%, 100% { transform: translateX(0px); }
          50% { transform: translateX(3px); }
        }
        
        @keyframes tilt {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(2deg); }
        }
        
        @keyframes flicker {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        
        @keyframes twinkle {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(1.1); filter: brightness(1.3); }
        }
        
        @keyframes pop {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        
        @keyframes celebrate {
          0%, 100% { transform: rotate(0deg) scale(1); }
          25% { transform: rotate(3deg) scale(1.05); }
          75% { transform: rotate(-3deg) scale(1.05); }
        }
        
        @keyframes launch {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-5px) rotate(5deg); }
        }
        
        /* Advanced Sticker Peel Effects */
        :root {
          --sticker-rotate: 5deg;
          --sticker-p: 8px;
          --sticker-peelback-hover: 25%;
          --sticker-peelback-active: 50%;
          --sticker-peel-easing: 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          --sticker-peel-hover-easing: 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          --sticker-start: calc(-1 * var(--sticker-p));
          --sticker-end: calc(100% + var(--sticker-p));
        }

        .sticker-container {
          position: relative;
          cursor: grab;
        }

        .sticker-container:active {
          cursor: grabbing;
        }

        .sticker-container * {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          -webkit-touch-callout: none;
          -webkit-tap-highlight-color: transparent;
        }

        .sticker-main {
          clip-path: polygon(
            var(--sticker-start) var(--sticker-start),
            var(--sticker-end) var(--sticker-start),
            var(--sticker-end) var(--sticker-end),
            var(--sticker-start) var(--sticker-end)
          );
          transition: clip-path var(--sticker-peel-hover-easing);
          filter: url(#stickerDropShadow);
        }

        .sticker-lighting {
          filter: url(#pointLight);
        }

        .sticker-container:hover .sticker-main {
          clip-path: polygon(
            var(--sticker-start) var(--sticker-peelback-hover),
            var(--sticker-end) var(--sticker-peelback-hover),
            var(--sticker-end) var(--sticker-end),
            var(--sticker-start) var(--sticker-end)
          );
          transition: clip-path var(--sticker-peel-hover-easing);
        }

        .sticker-container:active .sticker-main {
          clip-path: polygon(
            var(--sticker-start) var(--sticker-peelback-active),
            var(--sticker-end) var(--sticker-peelback-active),
            var(--sticker-end) var(--sticker-end),
            var(--sticker-start) var(--sticker-end)
          );
          transition: clip-path var(--sticker-peel-easing);
        }

        .sticker-emoji {
          transform: rotate(var(--sticker-rotate));
          filter: url(#outerStroke);
        }

        .sticker-image-photo {
          transform: rotate(var(--sticker-rotate));
        }

        .sticker-flap-emoji, .sticker-shadow-emoji {
          transform: rotate(var(--sticker-rotate));
          filter: url(#expandAndFill);
        }

        .sticker-flap-photo, .sticker-shadow-photo {
          transform: rotate(var(--sticker-rotate));
          filter: url(#expandAndFill);
        }

        .sticker-flap {
          position: absolute;
          width: 100%;
          height: 100%;
          left: 0;
          top: calc(-100% - var(--sticker-p) - var(--sticker-p));
          clip-path: polygon(
            var(--sticker-start) var(--sticker-start),
            var(--sticker-end) var(--sticker-start),
            var(--sticker-end) var(--sticker-start),
            var(--sticker-start) var(--sticker-start)
          );
          transform: scaleY(-1);
          transition: all var(--sticker-peel-hover-easing);
        }

        .sticker-container:hover .sticker-flap {
          clip-path: polygon(
            var(--sticker-start) var(--sticker-start),
            var(--sticker-end) var(--sticker-start),
            var(--sticker-end) var(--sticker-peelback-hover),
            var(--sticker-start) var(--sticker-peelback-hover)
          );
          top: calc(-100% + 2 * var(--sticker-peelback-hover) - 1px);
          transition: all var(--sticker-peel-hover-easing);
        }

        .sticker-container:active .sticker-flap {
          clip-path: polygon(
            var(--sticker-start) var(--sticker-start),
            var(--sticker-end) var(--sticker-start),
            var(--sticker-end) var(--sticker-peelback-active),
            var(--sticker-start) var(--sticker-peelback-active)
          );
          top: calc(-100% + 2 * var(--sticker-peelback-active) - 1px);
          transition: all var(--sticker-peel-easing);
        }

        .sticker-flap-lighting {
          filter: url(#pointLightFlipped);
        }

        .sticker-flap-image, .sticker-shadow-image {
          transform: rotate(var(--sticker-rotate));
          filter: url(#expandAndFill);
        }

        .sticker-shadow {
          position: absolute;
          top: 1rem;
          left: 0.5rem;
          width: 100%;
          height: 100%;
          filter: brightness(0) blur(8px);
          opacity: 0.2;
          pointer-events: none;
        }

        .sticker-main, .sticker-flap {
          will-change: clip-path, transform;
        }

        /* Sticker-specific styles */
        .sticker-item, .sticker-preview {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        
        .css-sticker-gentle-bounce { animation: gentle-bounce 2.5s ease-in-out infinite; }
        .css-sticker-heartbeat { animation: heartbeat 1.8s ease-in-out infinite; }
        .css-sticker-shake { animation: shake 2s ease-in-out infinite; }
        .css-sticker-slide { animation: slide 3s ease-in-out infinite; }
        .css-sticker-tilt { animation: tilt 4s ease-in-out infinite; }
        .css-sticker-flicker { animation: flicker 2s ease-in-out infinite; }
        .css-sticker-twinkle { animation: twinkle 2.5s ease-in-out infinite; }
        .css-sticker-pop { animation: pop 2s ease-in-out infinite; }
        .css-sticker-celebrate { animation: celebrate 1.5s ease-in-out infinite; }
        .css-sticker-launch { animation: launch 3s ease-in-out infinite; }

        /* Image Sticker Styles - Separate from emoji stickers */
        .image-sticker-container {
          position: relative;
          display: inline-block;
          --sticker-p: 8px;
          --sticker-start: var(--sticker-p);
          --sticker-end: calc(100% - var(--sticker-p));
          --sticker-peelback-hover: 15%;
          --sticker-peelback-active: 25%;
          --sticker-peel-easing: cubic-bezier(0.25, 0.46, 0.45, 0.94);
          --sticker-peel-hover-easing: cubic-bezier(0.25, 0.46, 0.45, 0.94);
          --sticker-rotate: 0deg;
          cursor: pointer;
        }

        .image-sticker-main {
          position: relative;
          clip-path: polygon(
            var(--sticker-start) var(--sticker-start),
            var(--sticker-end) var(--sticker-start),
            var(--sticker-end) var(--sticker-end),
            var(--sticker-start) var(--sticker-end)
          );
          transition: clip-path var(--sticker-peel-hover-easing);
          filter: url(#stickerDropShadow);
        }

        .image-sticker-lighting {
          filter: url(#pointLight);
        }

        .image-sticker-container:hover .image-sticker-main {
          clip-path: polygon(
            var(--sticker-start) var(--sticker-peelback-hover),
            var(--sticker-end) var(--sticker-peelback-hover),
            var(--sticker-end) var(--sticker-end),
            var(--sticker-start) var(--sticker-end)
          );
          transition: clip-path var(--sticker-peel-hover-easing);
        }

        .image-sticker-container:active .image-sticker-main {
          clip-path: polygon(
            var(--sticker-start) var(--sticker-peelback-active),
            var(--sticker-end) var(--sticker-peelback-active),
            var(--sticker-end) var(--sticker-end),
            var(--sticker-start) var(--sticker-end)
          );
          transition: clip-path var(--sticker-peel-easing);
        }

        .image-sticker-photo {
          transform: rotate(var(--sticker-rotate));
        }

        .image-sticker-flap {
          position: absolute;
          width: 100%;
          height: 100%;
          left: 0;
          top: calc(-100% - var(--sticker-p) - var(--sticker-p));
          clip-path: polygon(
            var(--sticker-start) var(--sticker-start),
            var(--sticker-end) var(--sticker-start),
            var(--sticker-end) var(--sticker-start),
            var(--sticker-start) var(--sticker-start)
          );
          transform: scaleY(-1);
          transition: all var(--sticker-peel-hover-easing);
        }

        .image-sticker-container:hover .image-sticker-flap {
          clip-path: polygon(
            var(--sticker-start) var(--sticker-start),
            var(--sticker-end) var(--sticker-start),
            var(--sticker-end) var(--sticker-peelback-hover),
            var(--sticker-start) var(--sticker-peelback-hover)
          );
          top: calc(-100% + 2 * var(--sticker-peelback-hover) - 1px);
          transition: all var(--sticker-peel-hover-easing);
        }

        .image-sticker-container:active .image-sticker-flap {
          clip-path: polygon(
            var(--sticker-start) var(--sticker-start),
            var(--sticker-end) var(--sticker-start),
            var(--sticker-end) var(--sticker-peelback-active),
            var(--sticker-start) var(--sticker-peelback-active)
          );
          top: calc(-100% + 2 * var(--sticker-peelback-active) - 1px);
          transition: all var(--sticker-peel-easing);
        }

        .image-sticker-flap-lighting {
          filter: url(#pointLightFlipped);
        }

        .image-sticker-flap-photo, .image-sticker-shadow-photo {
          transform: rotate(var(--sticker-rotate));
          filter: url(#expandAndFill);
        }

        .image-sticker-shadow {
          position: absolute;
          top: 1rem;
          left: 0.5rem;
          width: 100%;
          height: 100%;
          filter: brightness(0) blur(8px);
          opacity: 0.4;
          pointer-events: none;
        }

        .image-sticker-main, .image-sticker-flap {
          will-change: clip-path, transform;
        }
      `}</style>

      {/* Pie Menu for Item Interactions */}
      <PieMenu
        isOpen={pieMenu.isOpen}
        position={pieMenu.position}
        onClose={() => {
          pieMenu.close();
          setSelectedItemForMenu(null);
        }}
        items={[
          {
            id: 'like',
            icon: <Heart size={20} fill="currentColor" />,
            label: 'Like',
            color: 'bg-gradient-to-br from-pink-500 to-rose-600',
            onClick: async () => {
              if (!selectedItemForMenu || !user) return;
              try {
                const { data: existing } = await supabase
                  .from('canvas_item_reactions')
                  .select('id')
                  .eq('canvas_item_id', selectedItemForMenu)
                  .eq('user_id', user.id)
                  .eq('reaction_type', 'like')
                  .single();

                if (existing) {
                  await supabase
                    .from('canvas_item_reactions')
                    .delete()
                    .eq('id', existing.id);
                  toast.success('Unliked!');
                } else {
                  await supabase
                    .from('canvas_item_reactions')
                    .insert({
                      canvas_item_id: selectedItemForMenu,
                      user_id: user.id,
                      reaction_type: 'like'
                    });
                  toast.success('Liked! ❤️');
                }
              } catch (error) {
                console.error('Error toggling like:', error);
                toast.error('Failed to like');
              }
            }
          },
          {
            id: 'save',
            icon: <FolderPlus size={20} />,
            label: 'Save to Moodboard',
            color: 'bg-gradient-to-br from-purple-500 to-indigo-600',
            onClick: () => {
              toast.info('Moodboard feature coming soon!');
            }
          },
          {
            id: 'share',
            icon: <Share2 size={20} />,
            label: 'Share',
            color: 'bg-gradient-to-br from-blue-500 to-cyan-500',
            onClick: () => {
              const item = items.find(i => i.id === selectedItemForMenu);
              if (item) {
                const shareUrl = `${window.location.origin}/canvas?item=${item.id}&x=${Math.round(item.position.x)}&y=${Math.round(item.position.y)}`;
                navigator.clipboard.writeText(shareUrl);
                toast.success('Link copied to clipboard!');
              }
            }
          }
        ]}
      />
    </div>
  );
};

export default FomoCanvas;
